import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { tendersAPI } from "../services/api";
import { usePlanAccess } from "../components/PlanGate";
import { ArrowLeft, BookMarked, ExternalLink, Calendar, MapPin, Building2, Tag, DollarSign, CheckCircle, AlertTriangle, Zap, FileText, Shield, GitCompare, Lock } from "lucide-react";
import toast from "react-hot-toast";

// Display names for the canonical values in backend/app/core/tender_taxonomy.py.
// A value not listed here is shown as-is, so a vocabulary change on the server
// can never blank this page.
const TENDER_TYPE_LABELS = { GOODS: "Goods", WORKS: "Works", SERVICES: "Services", CONSULTANCY: "Consultancy" };
const PROCUREMENT_METHOD_LABELS = {
  OPEN: "Open competitive bidding", SELECTIVE: "Selective / restricted", LIMITED: "Limited tendering",
  DIRECT: "Direct / single-source", RFQ: "Request for quotations",
  // D16b: four NOCOPO wordings kept as values of their own, labelled in the source's words.
  NATIONAL_SHOPPING: "National Shopping", EMERGENCY: "Emergency",
  REPEAT_PROCUREMENT: "Repeat Procurement", DIRECT_LABOUR: "Direct Labour",
};

export default function TenderDetail() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const hasWinProb = usePlanAccess("win_probability");
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [winProb, setWinProb] = useState(null);
  const [winLoading, setWinLoading] = useState(false);

  useEffect(() => {
    tendersAPI.get(id).then(res => setTender(res.data)).catch(() => { toast.error("Tender not found"); navigate("/tenders"); }).finally(() => setLoading(false));
  }, [id, navigate]);

  const fetchWinProbability = async () => {
    setWinLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
      const res = await fetch(`${API}/ai/win-probability/${id}`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403) toast.error(data.detail || "Upgrade to Professional to use Win Probability.");
        else toast.error("Could not calculate win probability.");
        return;
      }
      setWinProb(data);
    } catch { toast.error("Win probability error."); }
    finally { setWinLoading(false); }
  };

  const handleSave = async () => {
    try {
      const res = await tendersAPI.save(id);
      setTender(prev => ({ ...prev, is_saved: res.data.saved }));
      toast.success(res.data.message);
    } catch { toast.error("Failed to save tender"); }
  };

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}><div style={{ color: "var(--text-muted)" }}>Loading tender...</div></div>;
  if (!tender) return null;

  const urgencyConfig = {
    critical: { color: "var(--accent-red)", dim: "var(--accent-red-dim)", label: "CRITICAL" },
    urgent: { color: "var(--accent-orange)", dim: "var(--accent-orange-dim)", label: "URGENT" },
    soon: { color: "var(--accent-blue)", dim: "var(--accent-blue-dim)", label: "SOON" },
    normal: { color: "var(--accent-green)", dim: "var(--accent-green-dim)", label: "NORMAL" },
  };
  const urg = urgencyConfig[tender.urgency] || urgencyConfig.normal;

  return (
    <div style={{ maxWidth: "900px" }}>
      <style>{`
  @media (max-width: 768px) {
    .mob-grid-2 { grid-template-columns: repeat(2,1fr) !important; }
    .mob-grid-1 { grid-template-columns: 1fr !important; }
    .mob-stack  { grid-template-columns: 1fr !important; }
    .mob-tabs   { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100% !important; }
    .mob-tabs::-webkit-scrollbar { display: none; }
    .mob-filters { grid-template-columns: 1fr !important; gap: 8px !important; }
  }
`}</style>
      <button onClick={() => navigate("/tenders")} style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "14px", marginBottom: "24px", padding: 0 }}>
        <ArrowLeft size={16} /> Back to Tenders
      </button>
      <div className="card" style={{ marginBottom: "20px", borderLeft: `4px solid ${urg.color}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
              <span style={{ background: urg.dim, color: urg.color, padding: "4px 12px", borderRadius: "100px", fontSize: "12px", fontWeight: "700" }}>{urg.label} • {tender.days_remaining != null ? `${tender.days_remaining} days left` : "No deadline"}</span>
              <span className="badge badge-open">{tender.category}</span>
              {tender.is_verified && <span className="badge" style={{ background: "var(--accent-blue-dim)", color: "var(--accent-blue)" }}>✓ Verified</span>}
            </div>
            <h1 style={{ fontSize: "22px", lineHeight: "1.4", marginBottom: "8px" }}>{tender.title}</h1>
            <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Ref: <span style={{ color: "var(--accent-green)", fontFamily: "monospace" }}>{tender.reference_number}</span></p>
          </div>
        </div>
        <div className="mob-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "16px" }}>
          {[
            { icon: DollarSign, label: "Contract Value", value: tender.contract_value_formatted || "Not Specified", color: "var(--accent-green)" },
            { icon: Calendar, label: "Deadline", value: tender.submission_deadline ? new Date(tender.submission_deadline).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : "N/A", color: urg.color },
            { icon: MapPin, label: "Location", value: tender.state, color: "var(--accent-blue)" },
            { icon: Building2, label: "Agency", value: tender.agency, color: "var(--accent-orange)" },
            // V2 Phase 2a classification: shown only when a source supplied it, never as "N/A".
            ...(tender.tender_type ? [{ icon: Tag, label: "Tender Type", value: TENDER_TYPE_LABELS[tender.tender_type] || tender.tender_type, color: "var(--accent-blue)" }] : []),
            ...(tender.procurement_method ? [{ icon: FileText, label: "Procurement Method", value: PROCUREMENT_METHOD_LABELS[tender.procurement_method] || tender.procurement_method, color: "var(--accent-green)" }] : []),
            ...(tender.industry ? [{ icon: Building2, label: "Industry", value: tender.industry, color: "var(--accent-orange)" }] : []),
            ...(tender.country ? [{ icon: MapPin, label: "Country", value: tender.country, color: "var(--accent-blue)" }] : []),
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} style={{ textAlign: "center", padding: "12px", background: "var(--bg-secondary)", borderRadius: "10px" }}>
              <Icon size={18} color={color} style={{ marginBottom: "6px" }} />
              <div style={{ fontSize: "12px", fontWeight: "600", marginBottom: "2px" }}>{value}</div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{label}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", paddingTop: "16px", borderTop: "1px solid var(--border)" }}>
          <button className="btn btn-ghost" style={{ padding: "8px 14px" }} onClick={handleSave}>
            <BookMarked size={15} color={tender.is_saved ? "var(--accent-green)" : undefined} />
            {tender.is_saved ? "Saved" : "Save"}
          </button>
          <button className="btn btn-primary" style={{ padding: "8px 14px", background: "var(--accent-orange)" }} onClick={() => navigate(`/bid-generator/${id}`)}>
            <Zap size={15} /> Generate Bid Doc
          </button>
          <button className="btn btn-ghost" style={{ padding: "8px 14px" }} onClick={() => navigate(`/compliance/${id}`)}>
            <Shield size={15} /> Compliance Check
          </button>
          <button className="btn btn-ghost" style={{ padding: "8px 14px" }} onClick={() => navigate(`/compare`)}>
            <GitCompare size={15} /> Compare
          </button>
          {tender.source_url && (
            <a href={tender.source_url} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ padding: "8px 14px" }}>
              <ExternalLink size={15} /> Source
            </a>
          )}
        </div>
      </div>
      <div className="mob-stack" style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className="card">
            <h3 style={{ fontSize: "16px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}><FileText size={16} color="var(--accent-green)" /> Description</h3>
            <p style={{ fontSize: "14px", color: "var(--text-secondary)", lineHeight: "1.8" }}>{tender.description}</p>
          </div>
          {tender.requirements && (
            <div className="card">
              <h3 style={{ fontSize: "16px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle size={16} color="var(--accent-blue)" /> Requirements</h3>
              <p style={{ fontSize: "14px", color: "var(--text-secondary)", lineHeight: "1.8" }}>{tender.requirements}</p>
            </div>
          )}
          {tender.eligibility && (
            <div className="card">
              <h3 style={{ fontSize: "16px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}><Tag size={16} color="var(--accent-orange)" /> Eligibility Criteria</h3>
              <p style={{ fontSize: "14px", color: "var(--text-secondary)", lineHeight: "1.8" }}>{tender.eligibility}</p>
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* ── Win Probability ── */}
          <div className="card" style={{ borderTop: "3px solid var(--accent-green)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <Zap size={15} color="var(--accent-green)" />
              <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-secondary)" }}>WIN PROBABILITY</span>
            </div>

            {/* Locked for Basic plan */}
            {!hasWinProb ? (
              <div style={{ textAlign: "center", padding: "16px 8px" }}>
                <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: "rgba(0,212,161,0.08)", border: "1px solid rgba(0,212,161,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                  <Lock size={18} color="var(--accent-green)" />
                </div>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "var(--text-secondary)", marginBottom: "6px" }}>Professional Plan Required</div>
                <p style={{ fontSize: "12px", color: "var(--text-muted)", lineHeight: "1.6", marginBottom: "14px" }}>
                  Get AI-powered win probability scores, strengths, risks and bid recommendations.
                </p>
                <button
                  onClick={() => navigate("/billing")}
                  style={{ width: "100%", padding: "9px", background: "var(--accent-green-dim)", border: "1px solid rgba(0,212,161,0.3)", borderRadius: "8px", color: "var(--accent-green)", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}
                >
                  Upgrade to Professional →
                </button>
              </div>
            ) : (
              <>
                {!winProb && !winLoading && (
                  <button onClick={fetchWinProbability} style={{ width: "100%", padding: "10px", background: "var(--accent-green-dim)", border: "1px solid var(--border-active)", borderRadius: "10px", color: "var(--accent-green)", fontSize: "13px", fontWeight: "700", cursor: "pointer" }}>
                    Analyse My Chances →
                  </button>
                )}
                {winLoading && (
                  <div style={{ textAlign: "center", padding: "16px", color: "var(--text-muted)", fontSize: "13px" }}>
                    Analysing...
                  </div>
                )}
                {winProb && (
                  <div>
                    <div style={{ textAlign: "center", marginBottom: "12px" }}>
                      <div style={{ fontSize: "44px", fontWeight: "900", color: winProb.probability >= 60 ? "var(--accent-green)" : winProb.probability >= 40 ? "var(--accent-orange)" : "var(--accent-red)" }}>
                        {winProb.probability}%
                      </div>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase" }}>{winProb.rating}</div>
                    </div>
                    <p style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "12px", lineHeight: "1.6", fontStyle: "italic" }}>{winProb.verdict}</p>
                    {winProb.strengths?.length > 0 && (
                      <div style={{ marginBottom: "10px" }}>
                        <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--accent-green)", marginBottom: "6px" }}>STRENGTHS</div>
                        {winProb.strengths.map((s, i) => (
                          <div key={i} style={{ fontSize: "12px", color: "var(--text-secondary)", display: "flex", gap: "6px", marginBottom: "4px" }}>
                            <span style={{ color: "var(--accent-green)", flexShrink: 0 }}>✓</span>{s}
                          </div>
                        ))}
                      </div>
                    )}
                    {winProb.risks?.length > 0 && (
                      <div style={{ marginBottom: "10px" }}>
                        <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--accent-red)", marginBottom: "6px" }}>RISKS</div>
                        {winProb.risks.map((r, i) => (
                          <div key={i} style={{ fontSize: "12px", color: "var(--text-secondary)", display: "flex", gap: "6px", marginBottom: "4px" }}>
                            <span style={{ color: "var(--accent-red)", flexShrink: 0 }}>⚠</span>{r}
                          </div>
                        ))}
                      </div>
                    )}
                    {winProb.recommendation && (
                      <div style={{ padding: "10px", background: "rgba(0,212,161,0.05)", border: "1px solid rgba(0,212,161,0.15)", borderRadius: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--accent-green)", marginBottom: "4px" }}>RECOMMENDATION</div>
                        <p style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: "1.6" }}>{winProb.recommendation}</p>
                      </div>
                    )}
                    <button onClick={fetchWinProbability} style={{ width: "100%", marginTop: "10px", padding: "8px", background: "transparent", border: "1px solid var(--border)", borderRadius: "8px", color: "var(--text-muted)", fontSize: "12px", cursor: "pointer" }}>
                      Recalculate
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          {tender.match_score && (
            <div className="card" style={{ background: "linear-gradient(135deg, rgba(0,212,161,0.1), rgba(79,142,247,0.05))", borderColor: "var(--border-active)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                <Zap size={16} color="var(--accent-green)" />
                <span style={{ fontSize: "14px", fontWeight: "700" }}>AI Match Score</span>
              </div>
              <div style={{ fontSize: "48px", fontFamily: "Syne", fontWeight: "800", color: "var(--accent-green)", lineHeight: 1 }}>{Math.round(tender.match_score)}</div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" }}>out of 100</div>
              <div style={{ marginTop: "12px", height: "6px", background: "var(--bg-secondary)", borderRadius: "3px" }}>
                <div style={{ height: "100%", width: `${tender.match_score}%`, background: "var(--accent-green)", borderRadius: "3px" }} />
              </div>
            </div>
          )}
          <div className="card">
            <h3 style={{ fontSize: "14px", marginBottom: "14px", color: "var(--text-secondary)" }}>TENDER DETAILS</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                { label: "Ministry", value: tender.ministry },
                { label: "Category", value: tender.category },
                { label: "Sub-Category", value: tender.sub_category },
                { label: "Currency", value: tender.currency },
                { label: "Published", value: tender.publication_date ? new Date(tender.publication_date).toLocaleDateString("en-NG") : "N/A" },
                { label: "Views", value: tender.view_count },
                { label: "Saves", value: tender.save_count },
              ].filter(i => i.value).map(({ label, value }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                  <span style={{ color: "var(--text-muted)" }}>{label}</span>
                  <span style={{ fontWeight: "500", textAlign: "right", maxWidth: "160px" }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
          {tender.days_remaining <= 14 && (
            <div className="card" style={{ background: urg.dim, borderColor: urg.color }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <AlertTriangle size={16} color={urg.color} />
                <span style={{ fontSize: "13px", fontWeight: "700", color: urg.color }}>Deadline Alert</span>
              </div>
              <p style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Only <strong style={{ color: urg.color }}>{tender.days_remaining} days</strong> remaining to submit your bid.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
